import { GetStaticPropsContext, GetStaticPaths, NextPage } from 'next';
import Link from 'next/link';
import { PathTypes, PostProps } from 'types';
import JoinDailyPolished from '@/components/pages/blog/JoinDailyPolishedFixed';
import Layout from '@/components/layout';
import SocialShare from '@/components/common/SocialShare';
import { formatRelativeTime } from '@/utilities/formatRelativeTime';
import { BlogPostingSchemaProps } from '@/components/common/Schema.org/BlogPosting';
// import getSocialImage from '@/lib/socialImage';
import { getBlogPost } from 'services/getBlogPost';
import { getBlogPosts } from 'services/getBlogPosts';
import { EnabledSEOProps, generateSeoData, SEOProps } from '@/lib/seoData';

interface Props {
  seo: SEOProps;
  schema: BlogPostingSchemaProps;
  article: PostProps;
  moreArticles: PostProps[];
}

interface BackLinkProps {
  href: string;
  label: string;
}

interface PostCategoryAndDateProps {
  category: string;
  date: string;
}

const BackLink = ({ href, label }: BackLinkProps) => (
  <Link href={href} passHref>
    <a className="text-silver hidden lg:block text-xs tracking-wide">{label}</a>
  </Link>
);

const PostCategoryAndDate = ({ category, date }: PostCategoryAndDateProps) => (
  <div className="flex flex-grow items-center">
    <p className="uppercase text-xs tracking-widest font-bold mr-2.5">
      {category}
    </p>
    <p className="text-xs tracking-wide text-onyx">&mdash; {date}</p>
  </div>
);

const BlogDetail: NextPage<Props> = (props) => {
  const { article, seo, schema, moreArticles } = props;
  return (
    <Layout seo={seo} schema={schema}>
      <div className="lg:grid lg:grid-cols-12 lg:mt-16 mb-10 lg:mb-24 lg:container lg:mx-auto">
        <div className="hidden lg:block mr-auto col-span-3 xl:pl-10 lg:pl-8 min-w-[347px]">
          <JoinDailyPolished />
        </div>
        <div className="col-span-2 xl:col-span-1"></div>
        <div className="flex flex-col lg:col-span-7 xl:col-span-8 overflow-hidden">
          <div className="px-8 lg:pr-8 lg:px-0">
            <BackLink href="/blog" label="&lt; Back" />
            <div className="text-silver flex lg:hidden mt-8 justify-between gap-1">
              <p className="text-sm mr-1 lg:mr-2">Share:</p>
              <div className="flex justify-around">
                <SocialShare slug={article.handle} />
              </div>
            </div>
            <hr className="border border-pearl lg:hidden my-7"></hr>
            <div className="flex mt-2 lg:mt-6">
              <PostCategoryAndDate
                category={article.tags.join(', ')}
                date={formatRelativeTime(new Date(article.publishedAt))}
              />
              <div className="text-silver hidden lg:flex ml-auto items-center justify-evenly gap-1">
                <p className="lg:text-xs mr-1 lg:mr-2">Share:</p>
                <SocialShare slug={article.handle} />
              </div>
            </div >
            <hr className="lg:mt-14 my-7 border-onyx hidden lg:block"></hr>

            <h1 className="lg:text-6xl text-3xl mt-5 mb-12 lg:mt-6 lg:mb-16">
              {article.title}
            </h1>
            {/* TODO: Replace with prose class */}
            <div
              className="body-text"
              dangerouslySetInnerHTML={{ __html: article.contentHtml }}
            />
            {/* <hr className="lg:mt-14 my-7 border-onyx"></hr>
            <h1 className="italic text-2xl mx-auto lg:mx-0 lg:mt-10">
              Related Stories
            </h1> */}
          </div >
          {/* <div className="flex flex-col lg:flex-row mt-5 mb-0 lg:mt-11">
            {morePosts.length ? (
              morePosts.map((post) => (
                <RelatedStoryCard
                  key={post.slug}
                  coverImageId={post.cover_image}
                  category={post.tags}
                  headline={post.title}
                  timePosted={formatRelativeTime(new Date(post.publishedAt))}
                  slug={post.slug}
                />
              ))
            ) : (
              <div className="flex flex-col lg:flex-row  mx-auto md:mx-0 px-8 lg:px-0 lg:mr-7 lg:mb-5">
                No Related Stories &mdash; Check back soon!
              </div>
            )}
          </div> */}
        </div>
      </div>
    </Layout>
  );
};

export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => {
  const articles = await getBlogPosts({ first: 100 })
  const paths: PathTypes = articles.map(
    (post: PostProps) => `/blog/${post.handle}`
  );
  return {
    paths: paths,
    fallback: false
  };
};

export const getStaticProps = async (context: GetStaticPropsContext) => {
  if (!context.params) {
    return;
  }
  const { slug } = context.params;

  const article = await getBlogPost({ slug: slug as string })

  const seo = generateSeoData(article.seo.title || article.title, article.seo.description || article.content) as EnabledSEOProps

  const schema: BlogPostingSchemaProps = {
    post: article,
    url: `https://dailypolished.com/blog/${slug}`,
    imageUrl: seo.openGraphImageUrl
  }

  return {
    props: {
      article,
      seo,
      schema,
      morePosts: []
    }
  };
};

export default BlogDetail;
